import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTenant } from '../../contexts/TenantContext'
import api, { getStorageUrl } from '../../services/apiService'
import '../../styles/client-admin/settings.scss'

const SiteSettings = () => {
  const navigate = useNavigate()
  const { tenant } = useTenant()
  const [activeTab, setActiveTab] = useState('general')
  const [loading, setLoading] = useState(false)
  const [loadingSettings, setLoadingSettings] = useState(true)
  const [savedMessage, setSavedMessage] = useState('')

  const [generalSettings, setGeneralSettings] = useState({
    siteName: '',
    siteDescription: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    timezone: 'Asia/Kolkata',
    language: 'en',
    dateFormat: 'DD/MM/YYYY'
  })

  const [brandingSettings, setBrandingSettings] = useState({
    organizationFullName: '',
    primaryColor: '#F05C16',
    secondaryColor: '#000000',
    accentColor: '#ffc107',
    logo: null,
    currentLogoPath: null,
    favicon: null,
    customCSS: ''
  })

  const [logoPreview, setLogoPreview] = useState(null)

  const [socialSettings, setSocialSettings] = useState({
    facebook: '',
    twitter: '',
    instagram: '',
    youtube: '',
    linkedin: '',
    website: ''
  })

  const [emailSettings, setEmailSettings] = useState({
    smtpHost: '',
    smtpPort: '587',
    smtpUsername: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: ''
  })

  const [securitySettings, setSecuritySettings] = useState({
    enableTwoFactor: false,
    sessionTimeout: '30',
    passwordPolicy: 'medium',
    allowRegistration: true,
    requireEmailVerification: true
  })

  const [teamSizeSettings, setTeamSizeSettings] = useState({
    playing_members: 0,
    extra_members: 0,
    coaches: 0,
    managers: 0,
    reg_id_prefix: ''
  })

  const [aboutPageSettings, setAboutPageSettings] = useState({
    about_title: 'About Us',
    about_content: '',
    about_image: null,
    currentImagePath: null
  })

  const [aboutImagePreview, setAboutImagePreview] = useState(null)

  const [governingBodySettings, setGoverningBodySettings] = useState({
    governing_body_title: 'Governing Body',
    governing_body_content: ''
  })

  // Governing Body Members with individual image uploads
  const [governingBodyMembers, setGoverningBodyMembers] = useState([])
  const [memberImagePreviews, setMemberImagePreviews] = useState({})

  // Gallery Settings - Tournament Based
  const [gallerySettings, setGallerySettings] = useState({
    gallery_title: 'Gallery',
    gallery_images: []
  })
  const [newGalleryImages, setNewGalleryImages] = useState([])
  const [galleryImagePreviews, setGalleryImagePreviews] = useState([])

  // Tournament Gallery
  const [tournaments, setTournaments] = useState([])
  const [selectedTournament, setSelectedTournament] = useState(null)
  const [tournamentImages, setTournamentImages] = useState([])
  const [newTournamentImages, setNewTournamentImages] = useState([])
  const [tournamentImagePreviews, setTournamentImagePreviews] = useState([])
  const [loadingTournaments, setLoadingTournaments] = useState(false)
  const [savingTournamentGallery, setSavingTournamentGallery] = useState(false)

  // News Settings
  const [newsSettings, setNewsSettings] = useState({
    news_title: 'News',
    news_items: []
  })

  // Homepage Banner Settings
  const [homepageSettings, setHomepageSettings] = useState({
    homepage_banners: []
  })
  const [pageBanners, setPageBanners] = useState({})
  const [pageBannerFiles, setPageBannerFiles] = useState({})
  const [pageBannerPreviews, setPageBannerPreviews] = useState({})
  const [bannerImagePreviews, setBannerImagePreviews] = useState({})
  const [newsImagePreviews, setNewsImagePreviews] = useState({})

  // Committees Settings
  const [committees, setCommittees] = useState([])
  const [savingCommittees, setSavingCommittees] = useState(false)

  // Downloads Settings
  const [downloads, setDownloads] = useState([])
  const [savingDownloads, setSavingDownloads] = useState(false)
  const [newDownloadTitle, setNewDownloadTitle] = useState('')
  const [newDownloadFile, setNewDownloadFile] = useState(null)

  // Accounts Settings
  const [accounts, setAccounts] = useState([])
  const [savingAccounts, setSavingAccounts] = useState(false)
  const [newAccountTitle, setNewAccountTitle] = useState('')
  const [newAccountFile, setNewAccountFile] = useState(null)

  useEffect(() => {
    loadGeneralSettings()
    loadBrandingSettings()
    loadTeamSizeSettings()
    loadAboutPageSettings()
    loadGoverningBodySettings()
    loadGallerySettings()
    loadNewsSettings()
    loadHomepageSettings()
    loadTournaments()
    loadCommitteesSettings()
    loadDownloadsSettings()
    loadAccountsSettings()
  }, [])

  const loadGeneralSettings = async () => {
    try {
      const response = await api.get('/client-admin/settings/general')
      const data = response.data

      if (data.success) {
        setGeneralSettings({
          siteName: data.data.site_name || '',
          siteDescription: data.data.site_description || '',
          contactEmail: data.data.contact_email || '',
          contactPhone: data.data.contact_phone || '',
          address: data.data.address || '',
          timezone: data.data.timezone || 'Asia/Kolkata',
          language: data.data.language || 'en',
          dateFormat: data.data.date_format || 'DD/MM/YYYY'
        })
      }
    } catch (error) {
      console.error('Error loading general settings:', error)
    }
  }

  const loadBrandingSettings = async () => {
    setLoadingSettings(true)
    try {
      const response = await api.get('/client-admin/settings')
      const data = response.data

      if (data.success) {
        setBrandingSettings(prev => ({
          ...prev,
          organizationFullName: data.data.organization_full_name || '',
          primaryColor: data.data.primary_color || '#F05C16',
          secondaryColor: data.data.secondary_color || '#000000',
          accentColor: data.data.accent_color || '#ffc107',
          currentLogoPath: data.data.logo || null
        }))
      }
    } catch (error) {
      console.error('Error loading settings:', error)
    } finally {
      setLoadingSettings(false)
    }
  }

  const loadTeamSizeSettings = async () => {
    try {
      const response = await api.get('/client-admin/settings/team-size')
      const data = response.data

      if (data.success) {
        setTeamSizeSettings({
          playing_members: data.data.playing_members || 0,
          extra_members: data.data.extra_members || 0,
          coaches: data.data.coaches || 0,
          managers: data.data.managers || 0,
          reg_id_prefix: data.data.reg_id_prefix || ''
        })
      }
    } catch (error) {
      console.error('Error loading team size settings:', error)
    }
  }

  const loadAboutPageSettings = async () => {
    try {
      const response = await api.get('/client-admin/settings/about-page')
      const data = response.data

      if (data.success) {
        setAboutPageSettings({
          about_title: data.data.about_title || 'About Us',
          about_content: data.data.about_content || '',
          about_image: null,
          currentImagePath: data.data.about_image || null
        })
      }
    } catch (error) {
      console.error('Error loading about page settings:', error)
    }
  }

  const loadGoverningBodySettings = async () => {
    try {
      const response = await api.get('/client-admin/settings/governing-body')
      const data = response.data

      if (data.success) {
        setGoverningBodySettings({
          governing_body_title: data.data.governing_body_title || 'Governing Body',
          governing_body_content: data.data.governing_body_content || ''
        })

        // Parse members from content or from members array
        if (data.data.governing_body_members && Array.isArray(data.data.governing_body_members)) {
          setGoverningBodyMembers(data.data.governing_body_members)
        } else if (data.data.governing_body_content) {
          // Parse from text format for backward compatibility
          const members = data.data.governing_body_content.split('\n')
            .filter(line => line.trim())
            .map((line, index) => {
              const parts = line.split('|').map(p => p.trim())
              return {
                id: index + 1,
                name: parts[0] || '',
                post: parts[1] || '',
                image: parts[2] || null,
                newImage: null
              }
            })
          setGoverningBodyMembers(members)
        }
      }
    } catch (error) {
      console.error('Error loading governing body settings:', error)
    }
  }

  const loadGallerySettings = async () => {
    try {
      const response = await api.get('/client-admin/settings/gallery')
      const data = response.data

      if (data.success) {
        setGallerySettings({
          gallery_title: data.data.gallery_title || 'Gallery',
          gallery_images: data.data.gallery_images || []
        })
      }
    } catch (error) {
      console.error('Error loading gallery settings:', error)
    }
  }

  // Load tournaments for gallery
  const loadTournaments = async () => {
    setLoadingTournaments(true)
    try {
      // Try gallery-list endpoint first, fall back to events list
      let events = []
      try {
        const galleryResponse = await api.get('/client-admin/events/gallery-list')
        if (galleryResponse.data.success) {
          events = galleryResponse.data.events || []
        }
      } catch {
        // Fall back to regular events list
        const response = await api.get('/client-admin/events')
        const data = response.data
        events = data.events?.data || data.data || []
      }

      // Sort by year descending
      const sorted = events.sort((a, b) => {
        const dateA = new Date(a.event_start_date || a.event_date || 0)
        const dateB = new Date(b.event_start_date || b.event_date || 0)
        return dateB - dateA
      })
      setTournaments(sorted)
    } catch (error) {
      console.error('Error loading tournaments:', error)
    } finally {
      setLoadingTournaments(false)
    }
  }

  // Load images for selected tournament
  const loadTournamentImages = async (eventId) => {
    try {
      const response = await api.get(`/client-admin/events/${eventId}`)
      const data = response.data
      console.log('Event data response:', data)

      if (data.success || data.event) {
        const event = data.event || data.data
        console.log('Event object:', event)
        console.log('Event images raw:', event?.event_images)

        let images = event?.event_images || []

        // Parse if it's a string
        if (typeof images === 'string') {
          try {
            images = JSON.parse(images)
          } catch {
            images = []
          }
        }

        // Ensure images is an array
        if (!Array.isArray(images)) {
          images = []
        }

        console.log('Final images array:', images)
        setTournamentImages(images)
      }
    } catch (error) {
      console.error('Error loading tournament images:', error)
      setTournamentImages([])
    }
  }

  // Handle tournament selection
  const handleTournamentSelect = (eventId) => {
    const tournament = tournaments.find(t => t.id === parseInt(eventId))
    setSelectedTournament(tournament)
    setNewTournamentImages([])
    setTournamentImagePreviews([])
    if (eventId) {
      loadTournamentImages(eventId)
    } else {
      setTournamentImages([])
    }
  }

  // Handle tournament image upload
  const handleTournamentImageUpload = (files) => {
    const fileArray = Array.from(files)
    const validFiles = fileArray.filter(file => {
      const isValid = file.type.startsWith('image/')
      const isSmallEnough = file.size <= 5 * 1024 * 1024 // 5MB limit
      return isValid && isSmallEnough
    })

    if (validFiles.length > 0) {
      const previews = validFiles.map(file => URL.createObjectURL(file))
      setTournamentImagePreviews(prev => [...prev, ...previews])
      setNewTournamentImages(prev => [...prev, ...validFiles])
    }
  }

  // Remove new tournament image
  const removeNewTournamentImage = (index) => {
    setNewTournamentImages(prev => prev.filter((_, i) => i !== index))
    setTournamentImagePreviews(prev => prev.filter((_, i) => i !== index))
  }

  // Save tournament gallery
  const handleSaveTournamentGallery = async () => {
    if (!selectedTournament) {
      setSavedMessage('Please select a tournament first')
      return
    }

    setSavingTournamentGallery(true)
    try {
      const formData = new FormData()

      // Add new images with the field name 'images[]' that the backend expects
      newTournamentImages.forEach((file) => {
        formData.append('images[]', file)
      })

      const response = await api.post(`/client-admin/events/${selectedTournament.id}/gallery`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      if (response.data.success) {
        setSavedMessage('Tournament gallery saved successfully!')
        setNewTournamentImages([])
        setTournamentImagePreviews([])
        // Update images from response
        if (response.data.images) {
          setTournamentImages(response.data.images)
        } else {
          loadTournamentImages(selectedTournament.id)
        }
      } else {
        setSavedMessage(`Error: ${response.data.message || 'Failed to save gallery'}`)
      }
    } catch (error) {
      console.error('Error saving tournament gallery:', error)
      setSavedMessage('Error: Failed to save tournament gallery')
    } finally {
      setSavingTournamentGallery(false)
      setTimeout(() => setSavedMessage(''), 3000)
    }
  }

  // Delete tournament image
  const handleDeleteTournamentImage = async (imageIndex) => {
    if (!selectedTournament) return

    try {
      const imageToDelete = tournamentImages[imageIndex]
      const imageUrl = typeof imageToDelete === 'string' ? imageToDelete : (imageToDelete.url || imageToDelete.path || imageToDelete)

      const response = await api.delete(`/client-admin/events/${selectedTournament.id}/gallery`, {
        data: { image_url: imageUrl }
      })

      if (response.data.success) {
        // Update from response or filter locally
        if (response.data.images) {
          setTournamentImages(response.data.images)
        } else {
          setTournamentImages(prev => prev.filter((_, i) => i !== imageIndex))
        }
        setSavedMessage('Image deleted successfully!')
      }
    } catch (error) {
      console.error('Error deleting image:', error)
      setSavedMessage('Error: Failed to delete image')
    }
    setTimeout(() => setSavedMessage(''), 3000)
  }

  // Group tournaments by year
  const getTournamentsByYear = () => {
    const grouped = {}
    tournaments.forEach(tournament => {
      const year = tournament.year
        || (tournament.event_start_date ? new Date(tournament.event_start_date).getFullYear() : null)
        || (tournament.event_date ? new Date(tournament.event_date).getFullYear() : null)
        || new Date().getFullYear()
      if (!grouped[year]) {
        grouped[year] = []
      }
      grouped[year].push(tournament)
    })
    // Sort years descending
    return Object.keys(grouped)
      .sort((a, b) => b - a)
      .reduce((acc, year) => {
        acc[year] = grouped[year]
        return acc
      }, {})
  }

  const loadNewsSettings = async () => {
    try {
      const response = await api.get('/client-admin/settings/news')
      const data = response.data

      if (data.success) {
        setNewsSettings({
          news_title: data.data.news_title || 'News',
          news_items: data.data.news_items || []
        })
      }
    } catch (error) {
      console.error('Error loading news settings:', error)
    }
  }

  const loadHomepageSettings = async () => {
    try {
      const response = await api.get('/client-admin/settings/homepage')
      const data = response.data

      if (data.success) {
        setHomepageSettings({
          homepage_banners: data.data.homepage_banners || []
        })
        if (data.data.page_banners) {
          setPageBanners(data.data.page_banners)
        }
      }
    } catch (error) {
      console.error('Error loading homepage settings:', error)
    }
  }

  // Load committees settings
  const loadCommitteesSettings = async () => {
    try {
      const response = await api.get('/client-admin/settings/committees')
      const data = response.data

      if (data.success) {
        setCommittees(data.data.committees || [])
      }
    } catch (error) {
      console.error('Error loading committees settings:', error)
    }
  }

  // Save committees settings
  const handleSaveCommittees = async () => {
    setSavingCommittees(true)
    setSavedMessage('')

    try {
      const response = await api.post('/client-admin/settings/committees', {
        committees: JSON.stringify(committees)
      })

      if (response.data.success) {
        setSavedMessage('Committees saved successfully!')
        setTimeout(() => setSavedMessage(''), 3000)
      } else {
        setSavedMessage(`Error: ${response.data.message || 'Failed to save committees'}`)
      }
    } catch (error) {
      console.error('Error saving committees:', error)
      setSavedMessage('Error: Failed to save committees')
    } finally {
      setSavingCommittees(false)
    }
  }

  // Add new committee
  const addCommittee = () => {
    const newCommittee = {
      id: Date.now(),
      name: 'New Committee',
      members: []
    }
    setCommittees([...committees, newCommittee])
  }

  // Delete committee
  const deleteCommittee = (committeeId) => {
    setCommittees(committees.filter(c => c.id !== committeeId))
  }

  // Update committee name
  const updateCommitteeName = (committeeId, name) => {
    setCommittees(committees.map(c =>
      c.id === committeeId ? { ...c, name } : c
    ))
  }

  // Add member to committee
  const addMemberToCommittee = (committeeId) => {
    setCommittees(committees.map(c =>
      c.id === committeeId
        ? { ...c, members: [...c.members, { designation: '', name: '', state: '' }] }
        : c
    ))
  }

  // Update member in committee
  const updateCommitteeMember = (committeeId, memberIndex, field, value) => {
    setCommittees(committees.map(c => {
      if (c.id === committeeId) {
        const updatedMembers = [...c.members]
        updatedMembers[memberIndex] = { ...updatedMembers[memberIndex], [field]: value }
        return { ...c, members: updatedMembers }
      }
      return c
    }))
  }

  // Delete member from committee
  const deleteMemberFromCommittee = (committeeId, memberIndex) => {
    setCommittees(committees.map(c => {
      if (c.id === committeeId) {
        const updatedMembers = c.members.filter((_, i) => i !== memberIndex)
        return { ...c, members: updatedMembers }
      }
      return c
    }))
  }

  // Load downloads settings
  const loadDownloadsSettings = async () => {
    try {
      const response = await api.get('/client-admin/settings/downloads')
      const data = response.data

      if (data.success) {
        setDownloads(data.data.downloads || [])
      }
    } catch (error) {
      console.error('Error loading downloads settings:', error)
    }
  }

  // Add new download
  const handleAddDownload = async () => {
    if (!newDownloadFile) {
      setSavedMessage('Please select a PDF file')
      return
    }

    setSavingDownloads(true)
    setSavedMessage('')

    try {
      const formData = new FormData()
      formData.append('pdf_file', newDownloadFile)
      formData.append('title', newDownloadTitle || newDownloadFile.name)

      const response = await api.post('/client-admin/settings/downloads', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (response.data.success) {
        setDownloads(response.data.data.downloads || [])
        setNewDownloadTitle('')
        setNewDownloadFile(null)
        // Clear file input
        const fileInput = document.getElementById('download-file-input')
        if (fileInput) fileInput.value = ''
        setSavedMessage('Download added successfully!')
        setTimeout(() => setSavedMessage(''), 3000)
      } else {
        setSavedMessage(`Error: ${response.data.message || 'Failed to add download'}`)
      }
    } catch (error) {
      console.error('Error adding download:', error)
      setSavedMessage('Error: Failed to add download')
    } finally {
      setSavingDownloads(false)
    }
  }

  // Delete download
  const handleDeleteDownload = async (downloadId) => {
    if (!window.confirm('Are you sure you want to delete this download?')) return

    try {
      const response = await api.delete('/client-admin/settings/downloads/item', {
        data: { download_id: downloadId }
      })

      if (response.data.success) {
        setDownloads(response.data.data.downloads || [])
        setSavedMessage('Download deleted successfully!')
        setTimeout(() => setSavedMessage(''), 3000)
      } else {
        setSavedMessage(`Error: ${response.data.message || 'Failed to delete download'}`)
      }
    } catch (error) {
      console.error('Error deleting download:', error)
      setSavedMessage('Error: Failed to delete download')
    }
  }

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A'
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  // Load accounts settings
  const loadAccountsSettings = async () => {
    try {
      const response = await api.get('/client-admin/settings/accounts')
      const data = response.data

      if (data.success) {
        setAccounts(data.data.accounts || [])
      }
    } catch (error) {
      console.error('Error loading accounts settings:', error)
    }
  }

  // Add new account document
  const handleAddAccount = async () => {
    if (!newAccountFile) {
      setSavedMessage('Please select a PDF file')
      return
    }

    setSavingAccounts(true)
    setSavedMessage('')

    try {
      const formData = new FormData()
      formData.append('pdf_file', newAccountFile)
      formData.append('title', newAccountTitle || newAccountFile.name)

      const response = await api.post('/client-admin/settings/accounts', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (response.data.success) {
        setAccounts(response.data.data.accounts || [])
        setNewAccountTitle('')
        setNewAccountFile(null)
        // Clear file input
        const fileInput = document.getElementById('account-file-input')
        if (fileInput) fileInput.value = ''
        setSavedMessage('Account document added successfully!')
        setTimeout(() => setSavedMessage(''), 3000)
      } else {
        setSavedMessage(`Error: ${response.data.message || 'Failed to add account document'}`)
      }
    } catch (error) {
      console.error('Error adding account document:', error)
      setSavedMessage('Error: Failed to add account document')
    } finally {
      setSavingAccounts(false)
    }
  }

  // Delete account document
  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this account document?')) return

    try {
      const response = await api.delete('/client-admin/settings/accounts/item', {
        data: { account_id: accountId }
      })

      if (response.data.success) {
        setAccounts(response.data.data.accounts || [])
        setSavedMessage('Account document deleted successfully!')
        setTimeout(() => setSavedMessage(''), 3000)
      } else {
        setSavedMessage(`Error: ${response.data.message || 'Failed to delete account document'}`)
      }
    } catch (error) {
      console.error('Error deleting account document:', error)
      setSavedMessage('Error: Failed to delete account document')
    }
  }

  const handleSave = async (settingsType) => {
    if (settingsType === 'General') {
      return handleSaveGeneral()
    }

    if (settingsType === 'Branding') {
      return handleSaveBranding()
    }

    if (settingsType === 'Team Size') {
      return handleSaveTeamSize()
    }

    if (settingsType === 'About Page') {
      return handleSaveAboutPage()
    }

    if (settingsType === 'Governing Body') {
      return handleSaveGoverningBody()
    }

    if (settingsType === 'Gallery') {
      return handleSaveGallery()
    }

    if (settingsType === 'News') {
      return handleSaveNews()
    }

    if (settingsType === 'Homepage') {
      return handleSaveHomepage()
    }

    setLoading(true)
    try {
      // Simulate API call for other tabs
      await new Promise(resolve => setTimeout(resolve, 1000))
      setSavedMessage(`${settingsType} settings saved successfully!`)
      setTimeout(() => setSavedMessage(''), 3000)
    } catch (error) {
      console.error('Error saving settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveGeneral = async () => {
    setLoading(true)
    setSavedMessage('')

    try {
      const response = await api.post('/client-admin/settings/general', {
        site_name: generalSettings.siteName,
        site_description: generalSettings.siteDescription,
        contact_email: generalSettings.contactEmail,
        contact_phone: generalSettings.contactPhone,
        address: generalSettings.address,
        timezone: generalSettings.timezone,
        language: generalSettings.language,
        date_format: generalSettings.dateFormat
      })

      if (response.data.success) {
        setSavedMessage('General settings saved successfully!')
        setTimeout(() => setSavedMessage(''), 3000)
      }
    } catch (error) {
      console.error('Error saving general settings:', error)
      setSavedMessage('Failed to save general settings. Please try again.')
      setTimeout(() => setSavedMessage(''), 3000)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveBranding = async () => {
    setLoading(true)
    setSavedMessage('')

    try {
      const formData = new FormData()
      formData.append('organization_full_name', brandingSettings.organizationFullName)
      formData.append('primary_color', brandingSettings.primaryColor)
      formData.append('secondary_color', brandingSettings.secondaryColor)
      formData.append('accent_color', brandingSettings.accentColor)

      if (brandingSettings.logo) {
        formData.append('logo', brandingSettings.logo)
      }

      const response = await api.post('/client-admin/settings', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      const data = response.data

      if (data.success) {
        setSavedMessage('Certificate branding settings saved successfully!')
        setTimeout(() => setSavedMessage(''), 3000)
        loadBrandingSettings() // Reload settings
      } else {
        setSavedMessage(`Error: ${data.message || 'Failed to save settings'}`)
      }
    } catch (error) {
      console.error('Error saving branding settings:', error)
      setSavedMessage('Error: Failed to save settings')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveTeamSize = async () => {
    setLoading(true)
    setSavedMessage('')

    try {
      const response = await api.post('/client-admin/settings/team-size', teamSizeSettings)
      const data = response.data

      if (data.success) {
        setSavedMessage('Team size settings saved successfully!')
        setTimeout(() => setSavedMessage(''), 3000)
      } else {
        setSavedMessage(`Error: ${data.message || 'Failed to save team size settings'}`)
      }
    } catch (error) {
      console.error('Error saving team size settings:', error)
      setSavedMessage('Error: Failed to save team size settings')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveAboutPage = async () => {
    setLoading(true)
    setSavedMessage('')

    try {
      const formData = new FormData()
      formData.append('about_title', aboutPageSettings.about_title)
      formData.append('about_content', aboutPageSettings.about_content)

      if (aboutPageSettings.about_image) {
        formData.append('about_image', aboutPageSettings.about_image)
      }

      // If image was removed (no preview and no current path), tell backend to remove it
      if (!aboutImagePreview && !aboutPageSettings.currentImagePath && !aboutPageSettings.about_image) {
        formData.append('remove_about_image', '1')
      }

      const response = await api.post('/client-admin/settings/about-page', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      const data = response.data

      if (data.success) {
        setSavedMessage('About page settings saved successfully!')
        setTimeout(() => setSavedMessage(''), 3000)
        loadAboutPageSettings()
      } else {
        setSavedMessage(`Error: ${data.message || 'Failed to save about page settings'}`)
      }
    } catch (error) {
      console.error('Error saving about page settings:', error)
      setSavedMessage('Error: Failed to save about page settings')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveGoverningBody = async () => {
    setLoading(true)
    setSavedMessage('')

    try {
      const formData = new FormData()
      formData.append('governing_body_title', governingBodySettings.governing_body_title)

      // Send members as JSON and handle images separately
      const membersData = governingBodyMembers.map((member, index) => ({
        id: member.id,
        name: member.name,
        post: member.post,
        image: member.image, // existing image path
        hasNewImage: !!member.newImage
      }))
      formData.append('governing_body_members', JSON.stringify(membersData))

      // Append new images
      governingBodyMembers.forEach((member, index) => {
        if (member.newImage) {
          formData.append(`member_image_${index}`, member.newImage)
        }
      })

      const response = await api.post('/client-admin/settings/governing-body', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      const data = response.data

      if (data.success) {
        setSavedMessage('Governing body settings saved successfully!')
        setTimeout(() => setSavedMessage(''), 3000)
        setMemberImagePreviews({}) // Clear previews after save
        loadGoverningBodySettings()
      } else {
        setSavedMessage(`Error: ${data.message || 'Failed to save governing body settings'}`)
      }
    } catch (error) {
      console.error('Error saving governing body settings:', error)
      setSavedMessage('Error: Failed to save governing body settings')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveGallery = async () => {
    setLoading(true)
    setSavedMessage('')

    try {
      const formData = new FormData()
      formData.append('gallery_title', gallerySettings.gallery_title)
      formData.append('gallery_images', JSON.stringify(gallerySettings.gallery_images))

      // Append new images
      newGalleryImages.forEach((file, index) => {
        formData.append(`gallery_image_${index}`, file)
      })

      const response = await api.post('/client-admin/settings/gallery', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      const data = response.data

      if (data.success) {
        setSavedMessage('Gallery settings saved successfully!')
        setTimeout(() => setSavedMessage(''), 3000)
        setNewGalleryImages([])
        setGalleryImagePreviews([])
        loadGallerySettings()
      } else {
        setSavedMessage(`Error: ${data.message || 'Failed to save gallery settings'}`)
      }
    } catch (error) {
      console.error('Error saving gallery settings:', error)
      setSavedMessage('Error: Failed to save gallery settings')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteGalleryImage = async (imageId) => {
    try {
      const response = await api.delete('/client-admin/settings/gallery/image', {
        data: { image_id: imageId }
      })
      const data = response.data

      if (data.success) {
        setGallerySettings(prev => ({
          ...prev,
          gallery_images: data.data.gallery_images
        }))
        setSavedMessage('Image deleted successfully!')
        setTimeout(() => setSavedMessage(''), 3000)
      }
    } catch (error) {
      console.error('Error deleting gallery image:', error)
      setSavedMessage('Error: Failed to delete image')
    }
  }

  const handleGalleryImageUpload = (files) => {
    const fileArray = Array.from(files)
    const validFiles = []
    const previews = []

    fileArray.forEach(file => {
      if (!file.type.startsWith('image/')) {
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        return
      }
      validFiles.push(file)

      const reader = new FileReader()
      reader.onloadend = () => {
        previews.push(reader.result)
        if (previews.length === validFiles.length) {
          setGalleryImagePreviews(prev => [...prev, ...previews])
        }
      }
      reader.readAsDataURL(file)
    })

    setNewGalleryImages(prev => [...prev, ...validFiles])
  }

  const removeNewGalleryImage = (index) => {
    setNewGalleryImages(prev => prev.filter((_, i) => i !== index))
    setGalleryImagePreviews(prev => prev.filter((_, i) => i !== index))
  }

  // News Management Functions
  const handleSaveNews = async () => {
    setLoading(true)
    setSavedMessage('')

    try {
      const formData = new FormData()
      formData.append('news_title', newsSettings.news_title)

      // Send news items as JSON and handle images/certificates separately
      const newsData = newsSettings.news_items.map((item, index) => ({
        id: item.id,
        title: item.title,
        excerpt: item.excerpt,
        content: item.content,
        image: item.image,
        certificate: item.certificate,
        date: item.date,
        hasNewImage: !!item.newImage,
        hasNewCertificate: !!item.newCertificate,
        remove_certificate: item.remove_certificate || false
      }))
      formData.append('news_items', JSON.stringify(newsData))

      // Append new images
      newsSettings.news_items.forEach((item, index) => {
        if (item.newImage) {
          formData.append(`news_image_${index}`, item.newImage)
        }
        if (item.newCertificate) {
          formData.append(`news_certificate_${index}`, item.newCertificate)
        }
      })

      const response = await api.post('/client-admin/settings/news', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      const data = response.data

      if (data.success) {
        setSavedMessage('News settings saved successfully!')
        setTimeout(() => setSavedMessage(''), 3000)
        setNewsImagePreviews({})
        loadNewsSettings()
      } else {
        setSavedMessage(`Error: ${data.message || 'Failed to save news settings'}`)
      }
    } catch (error) {
      console.error('Error saving news settings:', error)
      setSavedMessage('Error: Failed to save news settings')
    } finally {
      setLoading(false)
    }
  }

  // Homepage Banner Management Functions
  const handleSaveHomepage = async () => {
    setLoading(true)
    setSavedMessage('')

    try {
      const formData = new FormData()

      // Send banners as JSON and handle images separately
      const bannersData = homepageSettings.homepage_banners.map((banner, index) => ({
        id: banner.id,
        alt: banner.alt || `Banner ${index + 1}`,
        image: banner.image,
        link: banner.link || '',
        hasNewImage: !!banner.newImage
      }))
      formData.append('homepage_banners', JSON.stringify(bannersData))

      // Append new images
      homepageSettings.homepage_banners.forEach((banner, index) => {
        if (banner.newImage) {
          formData.append(`banner_image_${index}`, banner.newImage)
        }
      })

      // Append per-page banner files
      Object.entries(pageBannerFiles).forEach(([pageKey, file]) => {
        if (file) {
          formData.append(`page_banner_${pageKey}`, file)
        }
      })

      const response = await api.post('/client-admin/settings/homepage', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      const data = response.data

      if (data.success) {
        setSavedMessage('Homepage settings saved successfully!')
        setTimeout(() => setSavedMessage(''), 3000)
        setBannerImagePreviews({})
        setPageBannerFiles({})
        setPageBannerPreviews({})
        loadHomepageSettings()
      } else {
        setSavedMessage(`Error: ${data.message || 'Failed to save homepage settings'}`)
      }
    } catch (error) {
      console.error('Error saving homepage settings:', error)
      setSavedMessage('Error: Failed to save homepage settings')
    } finally {
      setLoading(false)
    }
  }

  const addBanner = () => {
    const newId = homepageSettings.homepage_banners.length > 0
      ? Math.max(...homepageSettings.homepage_banners.map(b => b.id)) + 1
      : 1
    setHomepageSettings(prev => ({
      ...prev,
      homepage_banners: [...prev.homepage_banners, {
        id: newId,
        alt: `Banner ${newId}`,
        image: null,
        link: '',
        newImage: null
      }]
    }))
  }

  const removeBanner = async (bannerId) => {
    const bannerToRemove = homepageSettings.homepage_banners.find(b => b.id === bannerId)

    // If banner has an image saved on server, delete it
    if (bannerToRemove && bannerToRemove.image && !bannerToRemove.newImage) {
      try {
        await api.delete('/client-admin/settings/homepage/banner', {
          data: { banner_id: bannerId }
        })
      } catch (error) {
        console.error('Error deleting banner from server:', error)
      }
    }

    setHomepageSettings(prev => ({
      ...prev,
      homepage_banners: prev.homepage_banners.filter(b => b.id !== bannerId)
    }))
    setBannerImagePreviews(prev => {
      const newPreviews = { ...prev }
      delete newPreviews[bannerId]
      return newPreviews
    })
  }

  const updateBanner = (bannerId, field, value) => {
    setHomepageSettings(prev => ({
      ...prev,
      homepage_banners: prev.homepage_banners.map(b =>
        b.id === bannerId ? { ...b, [field]: value } : b
      )
    }))
  }

  const handleBannerImageUpload = (bannerId, file) => {
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setSavedMessage('Please select an image file')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setSavedMessage('Image size should be less than 5MB')
      return
    }

    // Update banner with new image file
    setHomepageSettings(prev => ({
      ...prev,
      homepage_banners: prev.homepage_banners.map(b =>
        b.id === bannerId ? { ...b, newImage: file } : b
      )
    }))

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setBannerImagePreviews(prev => ({
        ...prev,
        [bannerId]: reader.result
      }))
    }
    reader.readAsDataURL(file)
  }

  const removeBannerImage = (bannerId) => {
    setHomepageSettings(prev => ({
      ...prev,
      homepage_banners: prev.homepage_banners.map(b =>
        b.id === bannerId ? { ...b, image: null, newImage: null } : b
      )
    }))
    setBannerImagePreviews(prev => {
      const newPreviews = { ...prev }
      delete newPreviews[bannerId]
      return newPreviews
    })
  }

  const addNewsItem = () => {
    const newId = newsSettings.news_items.length > 0
      ? Math.max(...newsSettings.news_items.map(n => parseInt(n.id) || 0)) + 1
      : 1
    const today = new Date().toISOString().split('T')[0]
    setNewsSettings(prev => ({
      ...prev,
      news_items: [...prev.news_items, {
        id: String(newId),
        title: '',
        excerpt: '',
        content: '',
        image: null,
        certificate: null,
        date: today,
        newImage: null,
        newCertificate: null
      }]
    }))
  }

  const removeNewsItem = async (id) => {
    // If item has been saved (has image path), delete via API
    const item = newsSettings.news_items.find(n => n.id === id)
    if (item && item.image && !item.newImage) {
      try {
        await api.delete('/client-admin/settings/news/item', {
          data: { news_id: id }
        })
      } catch (error) {
        console.error('Error deleting news item:', error)
      }
    }

    setNewsSettings(prev => ({
      ...prev,
      news_items: prev.news_items.filter(n => n.id !== id)
    }))
    setNewsImagePreviews(prev => {
      const newPreviews = { ...prev }
      delete newPreviews[id]
      return newPreviews
    })
  }

  const updateNewsItem = (id, field, value) => {
    setNewsSettings(prev => ({
      ...prev,
      news_items: prev.news_items.map(n =>
        n.id === id ? { ...n, [field]: value } : n
      )
    }))
  }

  const handleNewsImageUpload = (newsId, file) => {
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setSavedMessage('Please select an image file')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setSavedMessage('Image size should be less than 5MB')
      return
    }

    // Update news item with new image file
    setNewsSettings(prev => ({
      ...prev,
      news_items: prev.news_items.map(n =>
        n.id === newsId ? { ...n, newImage: file } : n
      )
    }))

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setNewsImagePreviews(prev => ({
        ...prev,
        [newsId]: reader.result
      }))
    }
    reader.readAsDataURL(file)
  }

  const removeNewsImage = (newsId) => {
    setNewsSettings(prev => ({
      ...prev,
      news_items: prev.news_items.map(n =>
        n.id === newsId ? { ...n, image: null, newImage: null } : n
      )
    }))
    setNewsImagePreviews(prev => {
      const newPreviews = { ...prev }
      delete newPreviews[newsId]
      return newPreviews
    })
  }

  const handleNewsCertificateUpload = (newsId, file) => {
    if (!file) return
    if (file.type !== 'application/pdf') {
      setSavedMessage('Please select a PDF file for the certificate')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setSavedMessage('Certificate PDF size should be less than 10MB')
      return
    }
    setNewsSettings(prev => ({
      ...prev,
      news_items: prev.news_items.map(n =>
        n.id === newsId ? { ...n, newCertificate: file } : n
      )
    }))
  }

  const removeNewsCertificate = (newsId) => {
    setNewsSettings(prev => ({
      ...prev,
      news_items: prev.news_items.map(n =>
        n.id === newsId ? { ...n, certificate: null, newCertificate: null, remove_certificate: true } : n
      )
    }))
  }

  // Governing Body Member Management Functions
  const addMember = () => {
    const newId = governingBodyMembers.length > 0
      ? Math.max(...governingBodyMembers.map(m => m.id)) + 1
      : 1
    setGoverningBodyMembers([...governingBodyMembers, {
      id: newId,
      name: '',
      post: '',
      image: null,
      newImage: null
    }])
  }

  const removeMember = (id) => {
    setGoverningBodyMembers(governingBodyMembers.filter(m => m.id !== id))
    setMemberImagePreviews(prev => {
      const newPreviews = { ...prev }
      delete newPreviews[id]
      return newPreviews
    })
  }

  const updateMember = (id, field, value) => {
    setGoverningBodyMembers(governingBodyMembers.map(m =>
      m.id === id ? { ...m, [field]: value } : m
    ))
  }

  const handleMemberImageUpload = (memberId, file) => {
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setSavedMessage('Please select an image file')
      return
    }

    if (file.size > 2 * 1024 * 1024) {
      setSavedMessage('Image size should be less than 2MB')
      return
    }

    // Update member with new image file
    setGoverningBodyMembers(governingBodyMembers.map(m =>
      m.id === memberId ? { ...m, newImage: file } : m
    ))

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setMemberImagePreviews(prev => ({
        ...prev,
        [memberId]: reader.result
      }))
    }
    reader.readAsDataURL(file)
  }

  const removeMemberImage = (memberId) => {
    setGoverningBodyMembers(governingBodyMembers.map(m =>
      m.id === memberId ? { ...m, image: null, newImage: null } : m
    ))
    setMemberImagePreviews(prev => {
      const newPreviews = { ...prev }
      delete newPreviews[memberId]
      return newPreviews
    })
  }

  const handleAboutImageUpload = (file) => {
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setSavedMessage('Please select an image file')
      return
    }

    if (file.size > 2 * 1024 * 1024) {
      setSavedMessage('Image size should be less than 2MB')
      return
    }

    setAboutPageSettings(prev => ({
      ...prev,
      about_image: file
    }))

    const reader = new FileReader()
    reader.onloadend = () => {
      setAboutImagePreview(reader.result)
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveAboutImage = () => {
    setAboutPageSettings(prev => ({
      ...prev,
      about_image: null,
      currentImagePath: null
    }))
    setAboutImagePreview(null)
  }

  const handleFileUpload = (field, file) => {
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setSavedMessage('Please select an image file')
      return
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setSavedMessage('Image size should be less than 2MB')
      return
    }

    setBrandingSettings(prev => ({
      ...prev,
      [field]: file
    }))

    // Create preview for logo
    if (field === 'logo') {
      const reader = new FileReader()
      reader.onloadend = () => {
        setLogoPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveLogo = () => {
    setBrandingSettings(prev => ({
      ...prev,
      logo: null,
      currentLogoPath: null
    }))
    setLogoPreview(null)
  }

  const tabs = [
    { id: 'general', label: 'General', icon: 'fas fa-cog' },
    { id: 'homepage', label: 'Homepage', icon: 'fas fa-home' },
    { id: 'teamsize', label: 'Team Size', icon: 'fas fa-users' },
    { id: 'aboutpage', label: 'About Page', icon: 'fas fa-info-circle' },
    { id: 'governingbody', label: 'Governing Body', icon: 'fas fa-landmark' },
    { id: 'committees', label: 'Committees', icon: 'fas fa-sitemap' },
    { id: 'gallery', label: 'Gallery', icon: 'fas fa-images' },
    { id: 'news', label: 'News', icon: 'fas fa-newspaper' },
    { id: 'downloads', label: 'Downloads', icon: 'fas fa-download' },
    { id: 'accounts', label: 'Accounts', icon: 'fas fa-file-invoice' }
    // Hidden tabs - uncomment when needed:
    // { id: 'branding', label: 'Branding', icon: 'fas fa-palette' },
    // { id: 'social', label: 'Social Media', icon: 'fas fa-share-alt' },
    // { id: 'email', label: 'Email', icon: 'fas fa-envelope' },
    // { id: 'security', label: 'Security', icon: 'fas fa-shield-alt' }
  ]

  return (
    <div className="site-settings">
      <div className="settings-header">
        <div className="header-icon">
          <i className="fas fa-cogs"></i>
        </div>
        <div className="header-content" style={{ flex: 1 }}>
          <h1>Site Settings</h1>
          <p>Configure and customize your {tenant?.name || 'organization'} website</p>
        </div>
        <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {savedMessage && (
            <div className="save-message">
              <i className="fas fa-check-circle"></i>
              {savedMessage}
            </div>
          )}
          <button
            onClick={() => navigate('/admin/dashboard')}
            style={{
              background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 100%)',
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              padding: '10px 20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.85rem',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(192, 15, 21, 0.4)'
            }}
          >
            <i className="fas fa-arrow-left"></i> Back
          </button>
        </div>
      </div>

      <div className="settings-container">
        <div className="settings-sidebar">
          <div className="tab-navigation">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={tab.icon}></i>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="settings-content">
          {activeTab === 'general' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>General Settings</h2>
                <p>Basic site information and preferences</p>
              </div>
              
              <div className="settings-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="siteName">
                      <i className="fas fa-home"></i>
                      Site Name
                    </label>
                    <input
                      type="text"
                      id="siteName"
                      value={generalSettings.siteName}
                      onChange={(e) => setGeneralSettings({ ...generalSettings, siteName: e.target.value })}
                      placeholder="Enter your organization name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="contactEmail">
                      <i className="fas fa-envelope"></i>
                      Contact Email
                    </label>
                    <input
                      type="email"
                      id="contactEmail"
                      value={generalSettings.contactEmail}
                      onChange={(e) => setGeneralSettings({ ...generalSettings, contactEmail: e.target.value })}
                      placeholder="contact@example.com"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="siteDescription">
                    <i className="fas fa-align-left"></i>
                    Site Description
                  </label>
                  <textarea
                    id="siteDescription"
                    value={generalSettings.siteDescription}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, siteDescription: e.target.value })}
                    placeholder="Describe your organization and its mission"
                    rows="3"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="contactPhone">
                      <i className="fas fa-phone"></i>
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      id="contactPhone"
                      value={generalSettings.contactPhone}
                      onChange={(e) => setGeneralSettings({ ...generalSettings, contactPhone: e.target.value })}
                      placeholder="+91-9876543210"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="timezone">
                      <i className="fas fa-clock"></i>
                      Timezone
                    </label>
                    <select
                      id="timezone"
                      value={generalSettings.timezone}
                      onChange={(e) => setGeneralSettings({ ...generalSettings, timezone: e.target.value })}
                    >
                      <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                      <option value="Asia/Mumbai">Asia/Mumbai (IST)</option>
                      <option value="Asia/Delhi">Asia/Delhi (IST)</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="address">
                    <i className="fas fa-map-marker-alt"></i>
                    Address
                  </label>
                  <textarea
                    id="address"
                    value={generalSettings.address}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, address: e.target.value })}
                    placeholder="Enter your organization's address"
                    rows="2"
                  />
                </div>

                <div className="form-actions">
                  <button 
                    className="btn-primary"
                    onClick={() => handleSave('General')}
                    disabled={loading}
                  >
                    {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                    Save General Settings
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'homepage' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Homepage Banners</h2>
                <p>Manage the banner slider images on your homepage</p>
              </div>

              <div className="settings-form">
                <div style={{ background: '#e8f4fd', border: '1px solid #b8daff', borderRadius: '8px', padding: '15px', marginBottom: '20px' }}>
                  <i className="fas fa-info-circle" style={{ color: '#0c5460', marginRight: '8px' }}></i>
                  <span style={{ color: '#0c5460' }}>
                    Add banner images for your homepage slider. Recommended size: 1920x600 pixels. Supported formats: JPG, PNG, WEBP.
                  </span>
                </div>

                <button
                  type="button"
                  onClick={addBanner}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '20px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  <i className="fas fa-plus"></i> Add Banner
                </button>

                {homepageSettings.homepage_banners.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', background: '#f9f9f9', borderRadius: '8px', border: '2px dashed #ddd' }}>
                    <i className="fas fa-image" style={{ fontSize: '48px', color: '#ccc', marginBottom: '15px' }}></i>
                    <p style={{ color: '#666', margin: 0 }}>No banners added yet. Click "Add Banner" to create your first homepage banner.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {homepageSettings.homepage_banners.map((banner, index) => (
                      <div key={banner.id} style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '20px',
                        background: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                          <h4 style={{ margin: 0, color: '#333' }}>
                            <i className="fas fa-image" style={{ marginRight: '8px', color: '#007bff' }}></i>
                            Banner {index + 1}
                          </h4>
                          <button
                            type="button"
                            onClick={() => removeBanner(banner.id)}
                            style={{
                              background: '#dc3545',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            <i className="fas fa-trash"></i> Remove
                          </button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '20px' }}>
                          {/* Banner Image Preview */}
                          <div>
                            <label style={{ fontSize: '12px', color: '#666', marginBottom: '8px', display: 'block' }}>Banner Image</label>
                            {bannerImagePreviews[banner.id] || banner.image ? (
                              <div style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
                                <img
                                  src={bannerImagePreviews[banner.id] || getStorageUrl(banner.image)}
                                  alt={banner.alt || 'Banner'}
                                  style={{ width: '100%', height: '150px', objectFit: 'cover', display: 'block' }}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeBannerImage(banner.id)}
                                  style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    background: 'rgba(220,53,69,0.9)',
                                    color: 'white',
                                    border: 'none',
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                            ) : (
                              <label style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '150px',
                                border: '2px dashed #ddd',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                background: '#f9f9f9',
                                transition: 'all 0.3s ease'
                              }}>
                                <i className="fas fa-cloud-upload-alt" style={{ fontSize: '32px', color: '#999', marginBottom: '10px' }}></i>
                                <span style={{ color: '#666', fontSize: '14px' }}>Click to upload</span>
                                <span style={{ color: '#999', fontSize: '12px' }}>1920x600px recommended</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleBannerImageUpload(banner.id, e.target.files[0])}
                                  style={{ display: 'none' }}
                                />
                              </label>
                            )}
                          </div>

                          {/* Banner Details */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div>
                              <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>Alt Text (for accessibility)</label>
                              <input
                                type="text"
                                value={banner.alt || ''}
                                onChange={(e) => updateBanner(banner.id, 'alt', e.target.value)}
                                placeholder="Describe the banner image..."
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px' }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>Link URL (optional)</label>
                              <input
                                type="text"
                                value={banner.link || ''}
                                onChange={(e) => updateBanner(banner.id, 'link', e.target.value)}
                                placeholder="https://example.com or /page-slug"
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px' }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Inner Page Banners Section */}
                <div style={{ marginTop: '30px', borderTop: '2px solid #e5e7eb', paddingTop: '25px' }}>
                  <h3 style={{ marginBottom: '10px', fontSize: '1.1rem', fontWeight: '600' }}>Inner Page Banners</h3>
                  <p style={{ color: '#6b7280', marginBottom: '15px', fontSize: '0.9rem' }}>
                    Upload separate banner images for each inner page. Recommended size: 1920x400 pixels.
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                    {[
                      { key: 'about', label: 'About Us' },
                      { key: 'governing-body', label: 'Governing Body' },
                      { key: 'states', label: 'States' },
                      { key: 'officials', label: 'Officials' },
                      { key: 'championships', label: 'Championships' },
                      { key: 'gallery', label: 'Gallery' },
                      { key: 'news', label: 'News' },
                      { key: 'contact', label: 'Contact Us' },
                      { key: 'committees', label: 'Committees' },
                      { key: 'downloads', label: 'Downloads' },
                      { key: 'rti', label: 'RTI' },
                      { key: 'accounts', label: 'Accounts' },
                      { key: 'verify-certificates', label: 'Verify Certificates' },
                      { key: 'rules', label: 'Rules' },
                    ].map(page => (
                      <div key={page.key} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', background: '#fafafa' }}>
                        <p style={{ fontWeight: '600', marginBottom: '8px', fontSize: '0.9rem' }}>{page.label}</p>
                        {(pageBannerPreviews[page.key] || pageBanners[page.key]) && (
                          <div style={{ marginBottom: '8px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #ddd', height: '100px' }}>
                            <img
                              src={pageBannerPreviews[page.key] || pageBanners[page.key]}
                              alt={`${page.label} Banner`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                          </div>
                        )}
                        <label style={{
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db',
                          borderRadius: '5px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', color: '#374151'
                        }}>
                          <i className="fas fa-upload"></i>
                          {pageBanners[page.key] ? 'Change' : 'Upload'}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files[0]
                              if (file) {
                                setPageBannerFiles(prev => ({ ...prev, [page.key]: file }))
                                setPageBannerPreviews(prev => ({ ...prev, [page.key]: URL.createObjectURL(file) }))
                              }
                            }}
                          />
                        </label>
                        {pageBannerPreviews[page.key] && (
                          <span style={{ marginLeft: '8px', color: '#059669', fontSize: '0.75rem' }}>
                            <i className="fas fa-check-circle"></i> New
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-actions" style={{ marginTop: '20px' }}>
                  <button
                    className="btn-primary"
                    onClick={() => handleSave('Homepage')}
                    disabled={loading}
                  >
                    {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                    Save Homepage Settings
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'teamsize' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Team Size Settings</h2>
                <p>Define allowed team size for associations</p>
              </div>

              <div className="settings-form">
                <div className="team-size-info-box">
                  <i className="fas fa-info-circle"></i>
                  <p>These settings define the maximum allowed team members for associations under this organization. This is a one-time setup that controls how many players, coaches, and managers an association can register.</p>
                </div>

                <div className="team-size-grid">
                  <div className="form-group team-size-field">
                    <label htmlFor="playing_members">
                      <i className="fas fa-running"></i>
                      Playing Members
                    </label>
                    <input
                      type="number"
                      id="playing_members"
                      value={teamSizeSettings.playing_members}
                      onChange={(e) => setTeamSizeSettings({ ...teamSizeSettings, playing_members: parseInt(e.target.value) || 0 })}
                      placeholder="0"
                      min="0"
                    />
                    <small className="help-text">Active players in the team</small>
                  </div>

                  <div className="form-group team-size-field">
                    <label htmlFor="extra_members">
                      <i className="fas fa-user-plus"></i>
                      Extra Members
                    </label>
                    <input
                      type="number"
                      id="extra_members"
                      value={teamSizeSettings.extra_members}
                      onChange={(e) => setTeamSizeSettings({ ...teamSizeSettings, extra_members: parseInt(e.target.value) || 0 })}
                      placeholder="0"
                      min="0"
                    />
                    <small className="help-text">Reserve or substitute players</small>
                  </div>

                  <div className="form-group team-size-field">
                    <label htmlFor="coaches">
                      <i className="fas fa-chalkboard-teacher"></i>
                      Coaches
                    </label>
                    <input
                      type="number"
                      id="coaches"
                      value={teamSizeSettings.coaches}
                      onChange={(e) => setTeamSizeSettings({ ...teamSizeSettings, coaches: parseInt(e.target.value) || 0 })}
                      placeholder="0"
                      min="0"
                    />
                    <small className="help-text">Team coaches and trainers</small>
                  </div>

                  <div className="form-group team-size-field">
                    <label htmlFor="managers">
                      <i className="fas fa-user-tie"></i>
                      Managers
                    </label>
                    <input
                      type="number"
                      id="managers"
                      value={teamSizeSettings.managers}
                      onChange={(e) => setTeamSizeSettings({ ...teamSizeSettings, managers: parseInt(e.target.value) || 0 })}
                      placeholder="0"
                      min="0"
                    />
                    <small className="help-text">Team managers and staff</small>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem', padding: '1.25rem', background: '#f0f9ff', borderRadius: '10px', border: '1px solid #bae6fd' }}>
                  <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: '#0369a1' }}>
                    <i className="fas fa-id-card" style={{ marginRight: '8px' }}></i>
                    Registration ID Prefix
                  </h3>
                  <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem' }}>
                    Set a prefix for member registration IDs. Example: If prefix is "TFI", IDs will be TFIP001 (Player), TFIC001 (Coach), TFIM001 (Manager)
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      value={teamSizeSettings.reg_id_prefix}
                      onChange={(e) => setTeamSizeSettings({ ...teamSizeSettings, reg_id_prefix: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') })}
                      placeholder="e.g. TFI, ABFI"
                      maxLength={10}
                      style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '1rem', fontWeight: '600', letterSpacing: '1px', width: '150px' }}
                    />
                    {teamSizeSettings.reg_id_prefix && (
                      <span style={{ fontSize: '0.85rem', color: '#475569' }}>
                        Preview: <strong>{teamSizeSettings.reg_id_prefix}P001</strong>, <strong>{teamSizeSettings.reg_id_prefix}C001</strong>, <strong>{teamSizeSettings.reg_id_prefix}M001</strong>
                      </span>
                    )}
                  </div>
                  {teamSizeSettings.reg_id_prefix && (
                    <button
                      style={{ marginTop: '0.75rem', padding: '0.5rem 1rem', background: '#0369a1', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500' }}
                      onClick={async () => {
                        if (!window.confirm('This will update ALL existing member registration IDs to the new format. Continue?')) return
                        try {
                          const res = await api.post('/client-admin/settings/regenerate-reg-ids')
                          alert(res.data.message || 'Registration IDs updated!')
                        } catch (err) {
                          alert(err.response?.data?.message || 'Failed to regenerate IDs')
                        }
                      }}
                    >
                      <i className="fas fa-sync-alt" style={{ marginRight: '6px' }}></i>
                      Regenerate All Existing IDs
                    </button>
                  )}
                </div>

                <div className="team-total-display">
                  <span>Total Team Size:</span>
                  <strong>{(teamSizeSettings.playing_members || 0) + (teamSizeSettings.extra_members || 0) + (teamSizeSettings.coaches || 0) + (teamSizeSettings.managers || 0)} members</strong>
                </div>

                <div className="form-actions">
                  <button
                    className="btn-primary"
                    onClick={() => handleSave('Team Size')}
                    disabled={loading}
                  >
                    {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                    Save Team Size Settings
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'aboutpage' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>About Page Settings</h2>
                <p>Customize your About Us page content and image</p>
              </div>

              <div className="settings-form">
                <div className="form-group">
                  <label htmlFor="about_title">
                    <i className="fas fa-heading"></i>
                    Page Title
                  </label>
                  <input
                    type="text"
                    id="about_title"
                    value={aboutPageSettings.about_title}
                    onChange={(e) => setAboutPageSettings({ ...aboutPageSettings, about_title: e.target.value })}
                    placeholder="About Us"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="about_content">
                    <i className="fas fa-align-left"></i>
                    Page Content (HTML supported)
                  </label>
                  <textarea
                    id="about_content"
                    value={aboutPageSettings.about_content}
                    onChange={(e) => setAboutPageSettings({ ...aboutPageSettings, about_content: e.target.value })}
                    placeholder="Enter your about page content here. You can use HTML tags like <p>, <strong>, <ul>, etc."
                    rows="12"
                    style={{ fontFamily: 'monospace', fontSize: '14px' }}
                  />
                  <small className="help-text">
                    <i className="fas fa-info-circle"></i>
                    You can use HTML tags for formatting: &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;li&gt;, etc.
                  </small>
                </div>

                <div className="divider"></div>

                <div className="form-section-title">
                  <h3><i className="fas fa-image"></i> About Page Image</h3>
                </div>

                <div className="logo-upload-container">
                  <div className="logo-preview-box">
                    {aboutImagePreview || aboutPageSettings.currentImagePath ? (
                      <div className="logo-preview-image">
                        <img
                          src={aboutImagePreview || getStorageUrl(aboutPageSettings.currentImagePath)}
                          alt="About Page"
                          style={{ maxWidth: '300px', maxHeight: '200px', objectFit: 'cover' }}
                        />
                        <button
                          type="button"
                          className="btn-remove-preview"
                          onClick={handleRemoveAboutImage}
                          title="Remove image"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ) : (
                      <div className="no-logo-placeholder">
                        <i className="fas fa-image"></i>
                        <p>No image uploaded</p>
                      </div>
                    )}
                  </div>

                  <div className="logo-upload-info">
                    <label htmlFor="about_image" className="btn-upload-logo">
                      <i className="fas fa-upload"></i>
                      {aboutPageSettings.currentImagePath || aboutImagePreview ? 'Change Image' : 'Upload Image'}
                    </label>
                    <input
                      type="file"
                      id="about_image"
                      accept="image/*"
                      onChange={(e) => handleAboutImageUpload(e.target.files[0])}
                      style={{ display: 'none' }}
                    />

                    <div className="logo-specs">
                      <h4>Image Specifications:</h4>
                      <ul>
                        <li><i className="fas fa-check"></i> Format: PNG, JPG, GIF</li>
                        <li><i className="fas fa-check"></i> Recommended: 800x400 pixels</li>
                        <li><i className="fas fa-check"></i> Max file size: 2MB</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    className="btn-primary"
                    onClick={() => handleSave('About Page')}
                    disabled={loading}
                  >
                    {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                    Save About Page Settings
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'governingbody' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Governing Body Settings</h2>
                <p>Manage your Governing Body members with photos</p>
              </div>

              <div className="settings-form">
                <div className="form-group">
                  <label htmlFor="governing_body_title">
                    <i className="fas fa-heading"></i>
                    Page Title
                  </label>
                  <input
                    type="text"
                    id="governing_body_title"
                    value={governingBodySettings.governing_body_title}
                    onChange={(e) => setGoverningBodySettings({ ...governingBodySettings, governing_body_title: e.target.value })}
                    placeholder="Governing Body"
                  />
                </div>

                <div className="divider"></div>

                <div className="form-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3><i className="fas fa-users"></i> Members ({governingBodyMembers.length})</h3>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={addMember}
                    style={{ padding: '8px 16px', fontSize: '14px' }}
                  >
                    <i className="fas fa-plus"></i> Add Member
                  </button>
                </div>

                {governingBodyMembers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
                    <i className="fas fa-users" style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px', display: 'block' }}></i>
                    <p style={{ color: '#666', marginBottom: '16px' }}>No members added yet</p>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={addMember}
                    >
                      <i className="fas fa-plus"></i> Add First Member
                    </button>
                  </div>
                ) : (
                  <div className="governing-body-members" style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
                    {governingBodyMembers.map((member, index) => (
                      <div key={member.id} className="member-card" style={{
                        display: 'flex',
                        gap: '16px',
                        padding: '16px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        alignItems: 'flex-start'
                      }}>
                        {/* Member Image */}
                        <div className="member-image-upload" style={{ flexShrink: 0 }}>
                          <div style={{
                            width: '100px',
                            height: '100px',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            background: '#e0e0e0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'
                          }}>
                            {memberImagePreviews[member.id] || member.image ? (
                              <>
                                <img
                                  src={memberImagePreviews[member.id] || getStorageUrl(member.image)}
                                  alt={member.name}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeMemberImage(member.id)}
                                  style={{
                                    position: 'absolute',
                                    top: '4px',
                                    right: '4px',
                                    background: 'rgba(255,0,0,0.8)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </>
                            ) : (
                              <i className="fas fa-user" style={{ fontSize: '32px', color: '#999' }}></i>
                            )}
                          </div>
                          <label
                            htmlFor={`member-image-${member.id}`}
                            style={{
                              display: 'block',
                              textAlign: 'center',
                              marginTop: '8px',
                              fontSize: '12px',
                              color: '#007bff',
                              cursor: 'pointer'
                            }}
                          >
                            <i className="fas fa-camera"></i> Upload Photo
                          </label>
                          <input
                            type="file"
                            id={`member-image-${member.id}`}
                            accept="image/*"
                            onChange={(e) => handleMemberImageUpload(member.id, e.target.files[0])}
                            style={{ display: 'none' }}
                          />
                        </div>

                        {/* Member Details */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div style={{ display: 'flex', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>Name *</label>
                              <input
                                type="text"
                                value={member.name}
                                onChange={(e) => updateMember(member.id, 'name', e.target.value)}
                                placeholder="Enter name"
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px' }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>Post/Designation *</label>
                              <input
                                type="text"
                                value={member.post}
                                onChange={(e) => updateMember(member.id, 'post', e.target.value)}
                                placeholder="e.g., President, Secretary"
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px' }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={() => removeMember(member.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#dc3545',
                            cursor: 'pointer',
                            padding: '8px',
                            fontSize: '16px'
                          }}
                          title="Remove member"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="form-actions">
                  <button
                    className="btn-primary"
                    onClick={() => handleSave('Governing Body')}
                    disabled={loading}
                  >
                    {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                    Save Governing Body Settings
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'committees' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Committees Management</h2>
                <p>Manage your organization's committees and their members</p>
              </div>

              <div className="settings-form">
                {/* Add Committee Button */}
                <div style={{ marginBottom: '24px' }}>
                  <button
                    type="button"
                    onClick={addCommittee}
                    className="btn-secondary"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                  >
                    <i className="fas fa-plus"></i> Add New Committee
                  </button>
                </div>

                {/* Committees List */}
                {committees.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    color: '#666'
                  }}>
                    <i className="fas fa-sitemap" style={{ fontSize: '48px', marginBottom: '16px', display: 'block', color: '#ccc' }}></i>
                    <p>No committees added yet. Click "Add New Committee" to get started.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {committees.map((committee, committeeIndex) => (
                      <div key={committee.id} style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        background: 'white'
                      }}>
                        {/* Committee Header */}
                        <div style={{
                          background: '#f8f9fa',
                          padding: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderBottom: '1px solid #e0e0e0'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                            <span style={{ fontWeight: '600', color: '#333', minWidth: '30px' }}>#{committeeIndex + 1}</span>
                            <input
                              type="text"
                              value={committee.name}
                              onChange={(e) => updateCommitteeName(committee.id, e.target.value)}
                              placeholder="Committee Name"
                              style={{
                                flex: 1,
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '16px',
                                fontWeight: '500'
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => deleteCommittee(committee.id)}
                            style={{
                              background: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '8px 16px',
                              cursor: 'pointer',
                              marginLeft: '12px'
                            }}
                          >
                            <i className="fas fa-trash"></i> Delete Committee
                          </button>
                        </div>

                        {/* Committee Members */}
                        <div style={{ padding: '16px' }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '16px'
                          }}>
                            <h4 style={{ margin: 0, color: '#333' }}>
                              <i className="fas fa-users"></i> Members ({committee.members?.length || 0})
                            </h4>
                            <button
                              type="button"
                              onClick={() => addMemberToCommittee(committee.id)}
                              style={{
                                background: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: '13px'
                              }}
                            >
                              <i className="fas fa-plus"></i> Add Member
                            </button>
                          </div>

                          {/* Members Table */}
                          {committee.members && committee.members.length > 0 ? (
                            <table style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              fontSize: '14px'
                            }}>
                              <thead>
                                <tr style={{ background: '#f8f9fa' }}>
                                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: '50px' }}>S.No</th>
                                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Designation</th>
                                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Name</th>
                                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>State</th>
                                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '80px' }}>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {committee.members.map((member, memberIndex) => (
                                  <tr key={memberIndex} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '10px', color: '#666' }}>{memberIndex + 1}</td>
                                    <td style={{ padding: '8px' }}>
                                      <input
                                        type="text"
                                        value={member.designation || ''}
                                        onChange={(e) => updateCommitteeMember(committee.id, memberIndex, 'designation', e.target.value)}
                                        placeholder="e.g., Chairman"
                                        style={{
                                          width: '100%',
                                          padding: '6px 10px',
                                          border: '1px solid #ddd',
                                          borderRadius: '4px'
                                        }}
                                      />
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                      <input
                                        type="text"
                                        value={member.name || ''}
                                        onChange={(e) => updateCommitteeMember(committee.id, memberIndex, 'name', e.target.value)}
                                        placeholder="Member Name"
                                        style={{
                                          width: '100%',
                                          padding: '6px 10px',
                                          border: '1px solid #ddd',
                                          borderRadius: '4px'
                                        }}
                                      />
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                      <input
                                        type="text"
                                        value={member.state || ''}
                                        onChange={(e) => updateCommitteeMember(committee.id, memberIndex, 'state', e.target.value)}
                                        placeholder="State"
                                        style={{
                                          width: '100%',
                                          padding: '6px 10px',
                                          border: '1px solid #ddd',
                                          borderRadius: '4px'
                                        }}
                                      />
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'center' }}>
                                      <button
                                        type="button"
                                        onClick={() => deleteMemberFromCommittee(committee.id, memberIndex)}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          color: '#dc3545',
                                          cursor: 'pointer',
                                          padding: '4px 8px'
                                        }}
                                        title="Remove member"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div style={{
                              textAlign: 'center',
                              padding: '20px',
                              color: '#999',
                              background: '#f8f9fa',
                              borderRadius: '4px'
                            }}>
                              No members added yet. Click "Add Member" to add members.
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="form-actions" style={{ marginTop: '24px' }}>
                  <button
                    className="btn-primary"
                    onClick={handleSaveCommittees}
                    disabled={savingCommittees}
                  >
                    {savingCommittees ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                    Save Committees
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'downloads' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Downloads Management</h2>
                <p>Upload PDF documents for users to download</p>
              </div>

              <div className="settings-form">
                {/* Add New Download */}
                <div style={{
                  background: '#f8f9fa',
                  padding: '20px',
                  borderRadius: '8px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '16px' }}>
                    <i className="fas fa-plus-circle"></i> Add New Download
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#555' }}>
                        Title <span style={{ color: '#999', fontWeight: 'normal' }}>(Display name for the document)</span>
                      </label>
                      <input
                        type="text"
                        value={newDownloadTitle}
                        onChange={(e) => setNewDownloadTitle(e.target.value)}
                        placeholder="e.g., ABFI Constitution, Annual Report 2024"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#555' }}>
                        PDF File <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input
                        id="download-file-input"
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setNewDownloadFile(e.target.files[0])}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px',
                          background: 'white'
                        }}
                      />
                      {newDownloadFile && (
                        <p style={{ margin: '8px 0 0', color: '#666', fontSize: '13px' }}>
                          <i className="fas fa-file-pdf" style={{ color: '#dc3545' }}></i>{' '}
                          {newDownloadFile.name} ({formatFileSize(newDownloadFile.size)})
                        </p>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={handleAddDownload}
                      disabled={savingDownloads || !newDownloadFile}
                      style={{
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '10px 20px',
                        cursor: savingDownloads || !newDownloadFile ? 'not-allowed' : 'pointer',
                        opacity: savingDownloads || !newDownloadFile ? 0.6 : 1,
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        alignSelf: 'flex-start'
                      }}
                    >
                      {savingDownloads ? (
                        <><i className="fas fa-spinner fa-spin"></i> Uploading...</>
                      ) : (
                        <><i className="fas fa-upload"></i> Upload PDF</>
                      )}
                    </button>
                  </div>
                </div>

                {/* Downloads List */}
                <div>
                  <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '16px' }}>
                    <i className="fas fa-list"></i> Current Downloads ({downloads.length})
                  </h3>

                  {downloads.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      color: '#666'
                    }}>
                      <i className="fas fa-download" style={{ fontSize: '48px', marginBottom: '16px', display: 'block', color: '#ccc' }}></i>
                      <p>No downloads added yet. Upload your first PDF document above.</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {downloads.map((download, index) => (
                        <div
                          key={download.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '16px',
                            background: 'white',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            gap: '16px'
                          }}
                        >
                          <div style={{
                            width: '50px',
                            height: '50px',
                            background: '#fef2f2',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <i className="fas fa-file-pdf" style={{ fontSize: '24px', color: '#dc3545' }}></i>
                          </div>

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h4 style={{ margin: '0 0 4px', color: '#333', fontSize: '15px' }}>
                              {download.title || download.file_name}
                            </h4>
                            <p style={{ margin: 0, color: '#999', fontSize: '13px' }}>
                              {download.file_name} • {formatFileSize(download.file_size)}
                              {download.created_at && (
                                <> • Added: {new Date(download.created_at).toLocaleDateString()}</>
                              )}
                            </p>
                          </div>

                          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                            <a
                              href={getStorageUrl(download.file_path)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                background: '#0d6efd',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '8px 12px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <i className="fas fa-eye"></i> View
                            </a>
                            <button
                              type="button"
                              onClick={() => handleDeleteDownload(download.id)}
                              style={{
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '8px 12px',
                                cursor: 'pointer',
                                fontSize: '13px'
                              }}
                            >
                              <i className="fas fa-trash"></i> Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'accounts' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Accounts Management</h2>
                <p>Upload PDF documents for accounts section</p>
              </div>

              <div className="settings-form">
                {/* Add New Account */}
                <div style={{
                  background: '#f8f9fa',
                  padding: '20px',
                  borderRadius: '8px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '16px' }}>
                    <i className="fas fa-plus-circle"></i> Add New Account Document
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#555' }}>
                        Title <span style={{ color: '#999', fontWeight: 'normal' }}>(Display name for the document)</span>
                      </label>
                      <input
                        type="text"
                        value={newAccountTitle}
                        onChange={(e) => setNewAccountTitle(e.target.value)}
                        placeholder="e.g., Annual Report 2024, Balance Sheet"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#555' }}>
                        PDF File <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input
                        id="account-file-input"
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setNewAccountFile(e.target.files[0])}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px',
                          background: 'white'
                        }}
                      />
                      {newAccountFile && (
                        <p style={{ margin: '8px 0 0', color: '#666', fontSize: '13px' }}>
                          <i className="fas fa-file-pdf" style={{ color: '#dc3545' }}></i>{' '}
                          {newAccountFile.name} ({formatFileSize(newAccountFile.size)})
                        </p>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={handleAddAccount}
                      disabled={savingAccounts || !newAccountFile}
                      style={{
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '10px 20px',
                        cursor: savingAccounts || !newAccountFile ? 'not-allowed' : 'pointer',
                        opacity: savingAccounts || !newAccountFile ? 0.6 : 1,
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        alignSelf: 'flex-start'
                      }}
                    >
                      {savingAccounts ? (
                        <><i className="fas fa-spinner fa-spin"></i> Uploading...</>
                      ) : (
                        <><i className="fas fa-upload"></i> Upload PDF</>
                      )}
                    </button>
                  </div>
                </div>

                {/* Accounts List */}
                <div>
                  <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '16px' }}>
                    <i className="fas fa-list"></i> Current Account Documents ({accounts.length})
                  </h3>

                  {accounts.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      color: '#666'
                    }}>
                      <i className="fas fa-file-invoice" style={{ fontSize: '48px', marginBottom: '16px', display: 'block', color: '#ccc' }}></i>
                      <p>No account documents added yet. Upload your first PDF document above.</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {accounts.map((account, index) => (
                        <div
                          key={account.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '16px',
                            background: 'white',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            gap: '16px'
                          }}
                        >
                          <div style={{
                            width: '50px',
                            height: '50px',
                            background: '#fef2f2',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <i className="fas fa-file-pdf" style={{ fontSize: '24px', color: '#dc3545' }}></i>
                          </div>

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h4 style={{ margin: '0 0 4px', color: '#333', fontSize: '15px' }}>
                              {account.title || account.file_name}
                            </h4>
                            <p style={{ margin: 0, color: '#999', fontSize: '13px' }}>
                              {account.file_name} • {formatFileSize(account.file_size)}
                              {account.created_at && (
                                <> • Added: {new Date(account.created_at).toLocaleDateString()}</>
                              )}
                            </p>
                          </div>

                          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                            <a
                              href={getStorageUrl(account.file_path)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                background: '#0d6efd',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '8px 12px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <i className="fas fa-eye"></i> View
                            </a>
                            <button
                              type="button"
                              onClick={() => handleDeleteAccount(account.id)}
                              style={{
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '8px 12px',
                                cursor: 'pointer',
                                fontSize: '13px'
                              }}
                            >
                              <i className="fas fa-trash"></i> Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Tournament Gallery</h2>
                <p>Manage photo gallery by tournaments - Images will be organized year-wise</p>
              </div>

              <div className="settings-form">
                {/* Tournament Selection */}
                <div className="form-group">
                  <label htmlFor="tournament_select">
                    <i className="fas fa-trophy"></i>
                    Select Tournament
                  </label>
                  <select
                    id="tournament_select"
                    value={selectedTournament?.id || ''}
                    onChange={(e) => handleTournamentSelect(e.target.value)}
                    style={{ width: '100%', padding: '12px', fontSize: '15px', border: '1px solid #ddd', borderRadius: '6px' }}
                  >
                    <option value="">-- Select a Tournament --</option>
                    {Object.entries(getTournamentsByYear()).map(([year, tournamentsInYear]) => (
                      <optgroup key={year} label={`Year ${year}`}>
                        {tournamentsInYear.map((tournament) => (
                          <option key={tournament.id} value={tournament.id}>
                            {tournament.event_name} - {tournament.event_venue || 'No venue'}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {loadingTournaments && (
                    <p style={{ color: '#666', marginTop: '8px', fontSize: '14px' }}>
                      <i className="fas fa-spinner fa-spin"></i> Loading tournaments...
                    </p>
                  )}
                </div>

                {/* Show tournament details when selected */}
                {selectedTournament && (
                  <>
                    <div style={{
                      background: '#f0f9ff',
                      border: '1px solid #0ea5e9',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '24px'
                    }}>
                      <h4 style={{ margin: '0 0 8px 0', color: '#0369a1' }}>
                        <i className="fas fa-info-circle"></i> Selected Tournament
                      </h4>
                      <p style={{ margin: 0, color: '#0c4a6e', fontWeight: '500' }}>
                        {selectedTournament.event_name}
                      </p>
                      <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>
                        <i className="fas fa-map-marker-alt"></i> {selectedTournament.event_venue || 'No venue'} |
                        <i className="fas fa-calendar" style={{ marginLeft: '12px' }}></i> {selectedTournament.event_start_date ? new Date(selectedTournament.event_start_date).toLocaleDateString() : 'No date'}
                      </p>
                    </div>

                    <div className="divider"></div>

                    {/* Add Images Section */}
                    <div className="form-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h3><i className="fas fa-images"></i> Tournament Images ({tournamentImages.length})</h3>
                      <label htmlFor="tournament_images_upload" className="btn-secondary" style={{ padding: '8px 16px', fontSize: '14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fas fa-plus"></i> Add Images
                      </label>
                      <input
                        type="file"
                        id="tournament_images_upload"
                        accept="image/*"
                        multiple
                        onChange={(e) => handleTournamentImageUpload(e.target.files)}
                        style={{ display: 'none' }}
                      />
                    </div>

                    {/* New Images Preview */}
                    {tournamentImagePreviews.length > 0 && (
                      <div style={{ marginBottom: '24px' }}>
                        <h4 style={{ marginBottom: '12px', color: '#28a745' }}>
                          <i className="fas fa-cloud-upload-alt"></i> New Images to Upload ({tournamentImagePreviews.length})
                        </h4>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                          gap: '16px'
                        }}>
                          {tournamentImagePreviews.map((preview, index) => (
                            <div key={`new-${index}`} style={{
                              position: 'relative',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              border: '2px dashed #28a745',
                              background: '#f8fff8'
                            }}>
                              <img
                                src={preview}
                                alt={`New ${index + 1}`}
                                style={{ width: '100%', height: '120px', objectFit: 'cover' }}
                              />
                              <button
                                type="button"
                                onClick={() => removeNewTournamentImage(index)}
                                style={{
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  background: 'rgba(220, 53, 69, 0.9)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '28px',
                                  height: '28px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                                title="Remove"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Existing Tournament Images */}
                    {tournamentImages.length === 0 && tournamentImagePreviews.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
                        <i className="fas fa-images" style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px', display: 'block' }}></i>
                        <p style={{ color: '#666', marginBottom: '16px' }}>No images for this tournament yet</p>
                        <label htmlFor="tournament_images_upload" className="btn-primary" style={{ cursor: 'pointer' }}>
                          <i className="fas fa-plus"></i> Add First Images
                        </label>
                      </div>
                    ) : (
                      <>
                        {tournamentImages.length > 0 && (
                          <div>
                            <h4 style={{ marginBottom: '12px', color: '#666' }}>
                              <i className="fas fa-check-circle"></i> Existing Images ({tournamentImages.length})
                            </h4>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                              gap: '16px',
                              marginBottom: '20px'
                            }}>
                              {tournamentImages.map((image, index) => (
                                <div key={index} style={{
                                  position: 'relative',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                  background: '#fff'
                                }}>
                                  <img
                                    src={getStorageUrl(typeof image === 'string' ? image : (image.url || image.path || '')) || ''}
                                    alt={`Tournament image ${index + 1}`}
                                    style={{ width: '100%', height: '120px', objectFit: 'cover' }}
                                    onError={(e) => {
                                      console.log('Image failed to load:', e.target.src)
                                      e.target.onerror = null
                                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="150" height="120" viewBox="0 0 150 120"><rect fill="%23f0f0f0" width="150" height="120"/><text fill="%23999" font-family="Arial" font-size="12" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">Image not found</text></svg>'
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteTournamentImage(index)}
                                    style={{
                                      position: 'absolute',
                                      top: '4px',
                                      right: '4px',
                                      background: 'rgba(220, 53, 69, 0.9)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '50%',
                                      width: '28px',
                                      height: '28px',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    title="Delete"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    <div className="form-actions">
                      <button
                        className="btn-primary"
                        onClick={handleSaveTournamentGallery}
                        disabled={savingTournamentGallery}
                      >
                        {savingTournamentGallery ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                        Save Tournament Gallery
                      </button>
                    </div>
                  </>
                )}

                {/* No tournament selected message */}
                {!selectedTournament && (
                  <div style={{ textAlign: 'center', padding: '60px 40px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <i className="fas fa-hand-point-up" style={{ fontSize: '48px', color: '#6b7280', marginBottom: '16px', display: 'block' }}></i>
                    <p style={{ color: '#666', fontSize: '16px' }}>Select a tournament above to manage its gallery images</p>
                    <p style={{ color: '#9ca3af', fontSize: '14px', marginTop: '8px' }}>
                      Images will be organized by year and tournament on the public gallery page
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'news' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>News Settings</h2>
                <p>Manage your news/blog posts</p>
              </div>

              <div className="settings-form">
                <div className="form-group">
                  <label htmlFor="news_title">
                    <i className="fas fa-heading"></i>
                    Page Title
                  </label>
                  <input
                    type="text"
                    id="news_title"
                    value={newsSettings.news_title}
                    onChange={(e) => setNewsSettings({ ...newsSettings, news_title: e.target.value })}
                    placeholder="News"
                  />
                </div>

                <div className="divider"></div>

                <div className="form-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3><i className="fas fa-newspaper"></i> News Articles ({newsSettings.news_items.length})</h3>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={addNewsItem}
                    style={{ padding: '8px 16px', fontSize: '14px' }}
                  >
                    <i className="fas fa-plus"></i> Add News Article
                  </button>
                </div>

                {newsSettings.news_items.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
                    <i className="fas fa-newspaper" style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px', display: 'block' }}></i>
                    <p style={{ color: '#666', marginBottom: '16px' }}>No news articles yet</p>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={addNewsItem}
                    >
                      <i className="fas fa-plus"></i> Add First Article
                    </button>
                  </div>
                ) : (
                  <div className="news-items-list" style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '20px' }}>
                    {newsSettings.news_items.map((item, index) => (
                      <div key={item.id} className="news-item-card" style={{
                        padding: '20px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                          <h4 style={{ margin: 0, color: '#333' }}>
                            <i className="fas fa-file-alt" style={{ marginRight: '8px', color: '#007bff' }}></i>
                            Article #{index + 1}
                          </h4>
                          <button
                            type="button"
                            onClick={() => removeNewsItem(item.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#dc3545',
                              cursor: 'pointer',
                              padding: '8px',
                              fontSize: '16px'
                            }}
                            title="Remove article"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>

                        <div style={{ display: 'flex', gap: '20px' }}>
                          {/* Image Upload */}
                          <div style={{ flexShrink: 0, width: '200px' }}>
                            <div style={{
                              width: '200px',
                              height: '130px',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              background: '#e0e0e0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative'
                            }}>
                              {newsImagePreviews[item.id] || item.image ? (
                                <>
                                  <img
                                    src={newsImagePreviews[item.id] || getStorageUrl(item.image)}
                                    alt={item.title}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeNewsImage(item.id)}
                                    style={{
                                      position: 'absolute',
                                      top: '4px',
                                      right: '4px',
                                      background: 'rgba(255,0,0,0.8)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '50%',
                                      width: '24px',
                                      height: '24px',
                                      cursor: 'pointer',
                                      fontSize: '12px'
                                    }}
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                </>
                              ) : (
                                <i className="fas fa-image" style={{ fontSize: '40px', color: '#999' }}></i>
                              )}
                            </div>
                            <label
                              htmlFor={`news-image-${item.id}`}
                              style={{
                                display: 'block',
                                textAlign: 'center',
                                marginTop: '8px',
                                fontSize: '12px',
                                color: '#007bff',
                                cursor: 'pointer'
                              }}
                            >
                              <i className="fas fa-camera"></i> Upload Image
                            </label>
                            <input
                              type="file"
                              id={`news-image-${item.id}`}
                              accept="image/*"
                              onChange={(e) => handleNewsImageUpload(item.id, e.target.files[0])}
                              style={{ display: 'none' }}
                            />
                          </div>

                          {/* News Details */}
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ display: 'flex', gap: '12px' }}>
                              <div style={{ flex: 2 }}>
                                <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>Title *</label>
                                <input
                                  type="text"
                                  value={item.title}
                                  onChange={(e) => updateNewsItem(item.id, 'title', e.target.value)}
                                  placeholder="Enter news title"
                                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px' }}
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>Date</label>
                                <input
                                  type="date"
                                  value={item.date}
                                  onChange={(e) => updateNewsItem(item.id, 'date', e.target.value)}
                                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px' }}
                                />
                              </div>
                            </div>

                            <div>
                              <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>Short Excerpt (for listing page)</label>
                              <textarea
                                value={item.excerpt}
                                onChange={(e) => updateNewsItem(item.id, 'excerpt', e.target.value)}
                                placeholder="Brief summary shown on news listing page..."
                                rows="2"
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', resize: 'vertical' }}
                              />
                            </div>

                            <div>
                              <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>Full Content (HTML supported)</label>
                              <textarea
                                value={item.content}
                                onChange={(e) => updateNewsItem(item.id, 'content', e.target.value)}
                                placeholder="Full article content. You can use HTML tags like <p>, <strong>, <ul>, etc."
                                rows="4"
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', resize: 'vertical', fontFamily: 'monospace', fontSize: '13px' }}
                              />
                            </div>

                            {/* Certificate PDF Upload */}
                            <div>
                              <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
                                <i className="fas fa-file-pdf" style={{ marginRight: '4px', color: '#dc3545' }}></i>
                                Certificate PDF (optional)
                              </label>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {(item.certificate || item.newCertificate) && !item.remove_certificate ? (
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 12px',
                                    background: '#f0fdf4',
                                    border: '1px solid #bbf7d0',
                                    borderRadius: '6px',
                                    flex: 1
                                  }}>
                                    <i className="fas fa-file-pdf" style={{ color: '#dc3545', fontSize: '18px' }}></i>
                                    <span style={{ fontSize: '13px', color: '#166534', flex: 1 }}>
                                      {item.newCertificate ? item.newCertificate.name : 'Certificate uploaded'}
                                    </span>
                                    {item.certificate && !item.newCertificate && (
                                      <a
                                        href={getStorageUrl(item.certificate)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ fontSize: '12px', color: '#007bff', textDecoration: 'none' }}
                                      >
                                        <i className="fas fa-external-link-alt"></i> View
                                      </a>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => removeNewsCertificate(item.id)}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#dc3545',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        fontSize: '14px'
                                      }}
                                      title="Remove certificate"
                                    >
                                      <i className="fas fa-times"></i>
                                    </button>
                                  </div>
                                ) : (
                                  <div style={{ flex: 1 }}>
                                    <label
                                      htmlFor={`news-cert-${item.id}`}
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '8px 16px',
                                        background: '#fff',
                                        border: '1px dashed #ccc',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        color: '#666',
                                        transition: 'all 0.2s'
                                      }}
                                    >
                                      <i className="fas fa-upload"></i> Upload PDF Certificate
                                    </label>
                                    <input
                                      type="file"
                                      id={`news-cert-${item.id}`}
                                      accept=".pdf"
                                      onChange={(e) => handleNewsCertificateUpload(item.id, e.target.files[0])}
                                      style={{ display: 'none' }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="form-actions">
                  <button
                    className="btn-primary"
                    onClick={() => handleSave('News')}
                    disabled={loading}
                  >
                    {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                    Save News Settings
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'branding' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Certificate Branding & Customization</h2>
                <p>Customize your organization's certificate design, logo, and colors</p>
              </div>

              {loadingSettings ? (
                <div className="loading-state">
                  <i className="fas fa-spinner fa-spin"></i> Loading settings...
                </div>
              ) : (
                <div className="settings-form">
                  {/* Organization Name */}
                  <div className="form-group">
                    <label htmlFor="organizationFullName">
                      <i className="fas fa-building"></i>
                      Full Organization Name *
                    </label>
                    <input
                      type="text"
                      id="organizationFullName"
                      value={brandingSettings.organizationFullName}
                      onChange={(e) => setBrandingSettings({ ...brandingSettings, organizationFullName: e.target.value })}
                      placeholder="e.g., Mumbai Cricket Association"
                      required
                    />
                    <small className="help-text">
                      <i className="fas fa-info-circle"></i>
                      This name will appear as the organizer on all certificates
                    </small>
                  </div>

                  <div className="divider"></div>

                  {/* Logo Upload Section */}
                  <div className="form-section-title">
                    <h3><i className="fas fa-image"></i> Organization Logo</h3>
                  </div>

                  <div className="logo-upload-container">
                    <div className="logo-preview-box">
                      {logoPreview || brandingSettings.currentLogoPath ? (
                        <div className="logo-preview-image">
                          <img
                            src={logoPreview || getStorageUrl(brandingSettings.currentLogoPath)}
                            alt="Organization Logo"
                          />
                          <button
                            type="button"
                            className="btn-remove-preview"
                            onClick={handleRemoveLogo}
                            title="Remove logo"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="no-logo-placeholder">
                          <i className="fas fa-image"></i>
                          <p>No logo uploaded</p>
                        </div>
                      )}
                    </div>

                    <div className="logo-upload-info">
                      <label htmlFor="logo" className="btn-upload-logo">
                        <i className="fas fa-upload"></i>
                        {brandingSettings.currentLogoPath || logoPreview ? 'Change Logo' : 'Upload Logo'}
                      </label>
                      <input
                        type="file"
                        id="logo"
                        accept="image/*"
                        onChange={(e) => handleFileUpload('logo', e.target.files[0])}
                        style={{ display: 'none' }}
                      />

                      <div className="logo-specs">
                        <h4>Logo Specifications:</h4>
                        <ul>
                          <li><i className="fas fa-check"></i> Format: PNG (transparent) or JPG</li>
                          <li><i className="fas fa-check"></i> Size: 300x300 pixels (square)</li>
                          <li><i className="fas fa-check"></i> Max file size: 2MB</li>
                          <li><i className="fas fa-check"></i> Position: Top-left on certificates</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="divider"></div>

                  {/* Certificate Colors */}
                  <div className="form-section-title">
                    <h3><i className="fas fa-palette"></i> Certificate Brand Colors</h3>
                  </div>

                  <div className="form-row">
                    <div className="form-group color-selector">
                      <label htmlFor="primaryColor">
                        <i className="fas fa-palette"></i>
                        Primary Color
                      </label>
                      <div className="color-input">
                        <input
                          type="color"
                          id="primaryColor"
                          value={brandingSettings.primaryColor}
                          onChange={(e) => setBrandingSettings({ ...brandingSettings, primaryColor: e.target.value })}
                          className="color-picker-input"
                        />
                        <input
                          type="text"
                          value={brandingSettings.primaryColor}
                          onChange={(e) => setBrandingSettings({ ...brandingSettings, primaryColor: e.target.value })}
                          placeholder="#F05C16"
                          className="color-code-input"
                        />
                      </div>
                      <small className="help-text">Used for event name, organizer text, and corner decorations (top-left & bottom-right)</small>
                    </div>

                    <div className="form-group color-selector">
                      <label htmlFor="secondaryColor">
                        <i className="fas fa-palette"></i>
                        Secondary Color
                      </label>
                      <div className="color-input">
                        <input
                          type="color"
                          id="secondaryColor"
                          value={brandingSettings.secondaryColor}
                          onChange={(e) => setBrandingSettings({ ...brandingSettings, secondaryColor: e.target.value })}
                          className="color-picker-input"
                        />
                        <input
                          type="text"
                          value={brandingSettings.secondaryColor}
                          onChange={(e) => setBrandingSettings({ ...brandingSettings, secondaryColor: e.target.value })}
                          placeholder="#000000"
                          className="color-code-input"
                        />
                      </div>
                      <small className="help-text">Used for corner decorations (top-right & bottom-left)</small>
                    </div>

                    <div className="form-group color-selector">
                      <label htmlFor="accentColor">
                        <i className="fas fa-palette"></i>
                        Accent Color
                      </label>
                      <div className="color-input">
                        <input
                          type="color"
                          id="accentColor"
                          value={brandingSettings.accentColor}
                          onChange={(e) => setBrandingSettings({ ...brandingSettings, accentColor: e.target.value })}
                          className="color-picker-input"
                        />
                        <input
                          type="text"
                          value={brandingSettings.accentColor}
                          onChange={(e) => setBrandingSettings({ ...brandingSettings, accentColor: e.target.value })}
                          placeholder="#ffc107"
                          className="color-code-input"
                        />
                      </div>
                      <small className="help-text">Used for rounded corner backgrounds</small>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      className="btn-primary btn-save-branding"
                      onClick={() => handleSave('Branding')}
                      disabled={loading || !brandingSettings.organizationFullName.trim()}
                    >
                      {loading ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          Save Certificate Settings
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'social' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Social Media Links</h2>
                <p>Connect your social media profiles</p>
              </div>
              
              <div className="settings-form">
                <div className="social-links-grid">
                  {Object.entries(socialSettings).map(([platform, url]) => (
                    <div key={platform} className="form-group social-input">
                      <label htmlFor={platform}>
                        <i className={`fab fa-${platform}`}></i>
                        {platform.charAt(0).toUpperCase() + platform.slice(1)}
                      </label>
                      <input
                        type="url"
                        id={platform}
                        value={url}
                        onChange={(e) => setSocialSettings({ ...socialSettings, [platform]: e.target.value })}
                        placeholder={`https://${platform}.com/yourpage`}
                      />
                    </div>
                  ))}
                </div>

                <div className="form-actions">
                  <button 
                    className="btn-primary"
                    onClick={() => handleSave('Social Media')}
                    disabled={loading}
                  >
                    {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                    Save Social Settings
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'email' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Email Configuration</h2>
                <p>Configure SMTP settings for email notifications</p>
              </div>
              
              <div className="settings-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="fromEmail">
                      <i className="fas fa-envelope"></i>
                      From Email
                    </label>
                    <input
                      type="email"
                      id="fromEmail"
                      value={emailSettings.fromEmail}
                      onChange={(e) => setEmailSettings({ ...emailSettings, fromEmail: e.target.value })}
                      placeholder="noreply@example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="fromName">
                      <i className="fas fa-user"></i>
                      From Name
                    </label>
                    <input
                      type="text"
                      id="fromName"
                      value={emailSettings.fromName}
                      onChange={(e) => setEmailSettings({ ...emailSettings, fromName: e.target.value })}
                      placeholder="Your Organization"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="smtpHost">
                      <i className="fas fa-server"></i>
                      SMTP Host
                    </label>
                    <input
                      type="text"
                      id="smtpHost"
                      value={emailSettings.smtpHost}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtpHost: e.target.value })}
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="smtpPort">
                      <i className="fas fa-plug"></i>
                      SMTP Port
                    </label>
                    <input
                      type="number"
                      id="smtpPort"
                      value={emailSettings.smtpPort}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtpPort: e.target.value })}
                      placeholder="587"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button 
                    className="btn-primary"
                    onClick={() => handleSave('Email')}
                    disabled={loading}
                  >
                    {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                    Save Email Settings
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Security Settings</h2>
                <p>Manage security and access control settings</p>
              </div>
              
              <div className="settings-form">
                <div className="security-options">
                  <div className="security-item">
                    <div className="security-info">
                      <h4>
                        <i className="fas fa-lock"></i>
                        Two-Factor Authentication
                      </h4>
                      <p>Add an extra layer of security to user accounts</p>
                    </div>
                    <div className="security-control">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={securitySettings.enableTwoFactor}
                          onChange={(e) => setSecuritySettings({ ...securitySettings, enableTwoFactor: e.target.checked })}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </div>

                  <div className="security-item">
                    <div className="security-info">
                      <h4>
                        <i className="fas fa-user-plus"></i>
                        Allow Registration
                      </h4>
                      <p>Allow new users to register on your site</p>
                    </div>
                    <div className="security-control">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={securitySettings.allowRegistration}
                          onChange={(e) => setSecuritySettings({ ...securitySettings, allowRegistration: e.target.checked })}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </div>

                  <div className="security-item">
                    <div className="security-info">
                      <h4>
                        <i className="fas fa-envelope-open-text"></i>
                        Email Verification
                      </h4>
                      <p>Require email verification for new accounts</p>
                    </div>
                    <div className="security-control">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={securitySettings.requireEmailVerification}
                          onChange={(e) => setSecuritySettings({ ...securitySettings, requireEmailVerification: e.target.checked })}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="sessionTimeout">
                      <i className="fas fa-clock"></i>
                      Session Timeout (minutes)
                    </label>
                    <input
                      type="number"
                      id="sessionTimeout"
                      value={securitySettings.sessionTimeout}
                      onChange={(e) => setSecuritySettings({ ...securitySettings, sessionTimeout: e.target.value })}
                      min="5"
                      max="120"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="passwordPolicy">
                      <i className="fas fa-key"></i>
                      Password Policy
                    </label>
                    <select
                      id="passwordPolicy"
                      value={securitySettings.passwordPolicy}
                      onChange={(e) => setSecuritySettings({ ...securitySettings, passwordPolicy: e.target.value })}
                    >
                      <option value="weak">Weak (6+ characters)</option>
                      <option value="medium">Medium (8+ chars, mixed case)</option>
                      <option value="strong">Strong (12+ chars, mixed, symbols)</option>
                    </select>
                  </div>
                </div>

                <div className="form-actions">
                  <button 
                    className="btn-primary"
                    onClick={() => handleSave('Security')}
                    disabled={loading}
                  >
                    {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                    Save Security Settings
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SiteSettings